<script setup lang="ts">
    import { useConfig } from '~/composables/storyblok-proxy/useConfig';
    import type { FachkreisPDPStoryblok } from '~storyblok-types/FachkreisPDPStoryblok';
    import type { ConfigStoryblok } from '~storyblok-types/ConfigStoryblok';
    import type { ExtendedISbStoryData } from '~/types/storyblokApi';

    const props = defineProps<LoginTeaserProps>();

    const { t } = useI18n();

    type LoginTeaserProps = Pick<FachkreisPDPStoryblok, 'loginTeaserHeadline' | 'loginTeaserText' | 'loginTeaserLink'> &
        Pick<ExtendedISbStoryData<unknown>, 'removedContents'>;

    const { data: story } = useConfig();
    useStoryblokBridgeRef(story);

    const loginModalController = useModal();

    const configContent = computed(() => story.value?.content as ConfigStoryblok);

    const teaserHeadline = computed(() => props.loginTeaserHeadline || configContent.value?.loginTeaserHeadline || '');

    const teaserLink = computed(() =>
        configContent.value?.loginTeaserLink?.id ? configContent.value.loginTeaserLink : null
    );

    const { renderToString } = useRichTextRenderer();

    function isEmptyHtml(html: string): boolean {
        const stripped = html
            .replace(/<[^/>][^>]*><\/[^>]+>/g, '')
            .replace(/\s|&nbsp;|<br\s*\/?>/gi, '')
            .trim();
        return stripped.length === 0;
    }

    const teaserDoc = computed(() => {
        const resolved = props.loginTeaserText;
        if (resolved) {
            const html = renderToString(resolved);
            if (!isEmptyHtml(html)) return resolved;
        }
        const fallback = configContent.value?.loginTeaserText;
        if (fallback) {
            const html = renderToString(fallback);
            if (!isEmptyHtml(html)) return fallback;
        }
        return null;
    });
</script>

<template>
    <div class="login-teaser">
        <div class="login-teaser-content">
            <div class="login-teaser-wrapper">
                <div class="login-teaser-headline">
                    <img
                        class="login-teaser-icon"
                        src="~/assets/icons/lock-keyhole-white.svg"
                        aria-hidden="true"
                    />
                    <p>{{ teaserHeadline }}</p>
                </div>

                <SharedIconButton
                    v-if="teaserLink"
                    :aria-label="teaserLink.title"
                    :href="useStoryblokUrl(teaserLink)"
                    size="md"
                    icon="lucide:info"
                    variant="white"
                />
            </div>

            <div class="login-teaser-text-wrapper">
                <div class="login-teaser-text">
                    <RichText
                        v-if="teaserDoc"
                        :doc="teaserDoc"
                    />
                </div>

                <PrimaryButton
                    :button-text="t('fachkreis.loginTeaser.login')"
                    native-button
                    size="small"
                    data-testid="fachkreis-login-teaser-sign-in-button"
                    @click="loginModalController.open"
                />
            </div>
        </div>
    </div>

    <SharedLoginModal :login-modal-controller="loginModalController" />
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/login-teaser/login-teaser.scss';
</style>
